// Light/dark switching for DevRobot apps, with a circular reveal from the toggle (DevRobot UI kit).
// "system" leaves <html> without data-theme, so the OS setting decides.

import { reducedMotion } from './motion';

export const THEMES = { light: 'devrobot', dark: 'devrobot-night' } as const;
export type ThemeName = (typeof THEMES)[keyof typeof THEMES];
export type ThemeChoice = ThemeName | 'system';

const KEY = 'devrobot:theme';
const LEGACY_KEY = 'devrobot-theme';
const root = () => document.documentElement;
const media = () => (typeof matchMedia === 'function' ? matchMedia('(prefers-color-scheme: dark)') : null);

const isTheme = (v: unknown): v is ThemeName => v === THEMES.light || v === THEMES.dark;

export function readChoice(): ThemeChoice {
  try {
    const v = localStorage.getItem(KEY);
    if (isTheme(v)) return v;
    // Earlier releases stored "devrobot" | "devrobot-dark" under another key.
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy === 'devrobot-dark') return THEMES.dark;
    if (legacy === 'devrobot') return THEMES.light;
  } catch {
    /* storage unavailable */
  }
  return 'system';
}

function writeChoice(choice: ThemeChoice) {
  try {
    localStorage.removeItem(LEGACY_KEY);
    if (choice === 'system') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, choice);
  } catch {
    /* storage unavailable (private mode, sandbox): the choice lasts for this visit */
  }
}

export function currentTheme(): ThemeName {
  const pinned = root().dataset.theme;
  if (isTheme(pinned)) return pinned;
  return media()?.matches ? THEMES.dark : THEMES.light;
}

const announce = () => {
  syncThemeColor();
  document.dispatchEvent(new CustomEvent('dr:theme', { detail: currentTheme() }));
};

// Browser chrome (Chrome's toolbar, the iOS Home Screen app's status bar) follows <meta name="theme-color">.
// index.html ships one per colour scheme; a pinned theme overrides both with its canvas colour,
// resolved to hex through a 1×1 canvas because theme-color doesn't take oklch everywhere.
let probe: CanvasRenderingContext2D | null | undefined;
function syncThemeColor() {
  const metas = document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]');
  if (!metas.length) return;
  try {
    probe ??= document.createElement('canvas').getContext('2d', { willReadFrequently: true });
  } catch {
    probe = null;
  }
  const value = getComputedStyle(root()).getPropertyValue('--color-canvas').trim();
  if (!probe || !value) return;
  probe.clearRect(0, 0, 1, 1);
  probe.fillStyle = value;
  probe.fillRect(0, 0, 1, 1);
  const [r, g, b] = probe.getImageData(0, 0, 1, 1).data;
  const hex = `#${[r, g, b].map((n) => n.toString(16).padStart(2, '0')).join('')}`;
  metas.forEach((m) => (m.content = hex));
}

let initialized = false;
export function initTheme() {
  if (initialized) return;
  initialized = true;
  const choice = readChoice();
  if (choice === 'system') delete root().dataset.theme;
  else root().dataset.theme = choice;
  new MutationObserver(announce).observe(root(), { attributes: true, attributeFilter: ['data-theme'] });
  media()?.addEventListener('change', announce);
  announce();
}

/** Apply a theme choice; with `origin` (the toggle's centre) a circle reveals the new theme from there. */
export async function setTheme(next: ThemeChoice, origin?: { x: number; y: number }) {
  const apply = () => {
    if (next === 'system') delete root().dataset.theme;
    else root().dataset.theme = next;
    writeChoice(next);
    document.dispatchEvent(new CustomEvent('dr:theme-choice', { detail: next }));
  };
  const resolved = next === 'system' ? (media()?.matches ? THEMES.dark : THEMES.light) : next;
  if (!document.startViewTransition || reducedMotion() || resolved === currentTheme()) return apply();

  const x = origin?.x ?? innerWidth - 48;
  const y = origin?.y ?? 32;
  // The clip is written in percentages of the snapshot, never pixels: on HiDPI screens Chrome
  // sometimes lays the snapshot out in device pixels.
  const w = innerWidth;
  const h = innerHeight;
  const cx = `${(x / w) * 100}%`;
  const cy = `${(y / h) * 100}%`;
  // Farthest corner plus a margin, as a percentage of circle()'s reference length √(w²+h²)/√2.
  const farthest = Math.hypot(Math.max(x, w - x), Math.max(y, h - y)) + 48;
  const radius = `${(farthest / (Math.hypot(w, h) / Math.SQRT2)) * 100}%`;
  // devrobot.css starts the new snapshot fully clipped and holds the transition open;
  // the clip grows from the toggle and the transition ends once it covers the screen.
  root().classList.add('dr-theme-reveal');
  const vt = document.startViewTransition(apply);
  try {
    await vt.ready;
    await root().animate(
      { clipPath: [`circle(0% at ${cx} ${cy})`, `circle(${radius} at ${cx} ${cy})`] },
      { duration: 760, easing: 'cubic-bezier(0.33, 0, 0.2, 1)', fill: 'forwards', pseudoElement: '::view-transition-new(root)' },
    ).finished;
  } catch {
    /* animation unsupported or transition skipped: the theme is applied either way */
  } finally {
    vt.skipTransition();
    await vt.finished.catch(() => {});
    root().classList.remove('dr-theme-reveal');
  }
}

export const toggleTheme = (origin?: { x: number; y: number }) => setTheme(currentTheme() === THEMES.dark ? THEMES.light : THEMES.dark, origin);
